import { useEffect, useRef, useState } from "react";
import { Home, LayoutDashboard, ShoppingBag, Trophy, LogOut } from "lucide-react";

const SITE_PAGES = [
  { path: "/", label: "Kezdőlap", icon: Home },
  { path: "/ugyfelkapu", label: "Ügyfélkapu", icon: LayoutDashboard },
  { path: "/shop", label: "Shop", icon: ShoppingBag },
  { path: "/verseny", label: "Verseny", icon: Trophy },
];
const normalize = (p) => decodeURIComponent(p || "/").replace(/\/+$/, "") || "/";
const pageMatches = (page, current) => page.path === "/" ? current === "/" : current === page.path || current.startsWith(page.path + "/") || (page.path === "/ugyfelkapu" && current === "/ügyfelkapu");

export const resolveGender = (user) => {
  if (user?.is_admin) return "neutral";
  const g = (user?.gender || "").toLowerCase();
  if (["no","nő","female","lany","lány","girl","f"].includes(g)) return "female";
  if (["ferfi","férfi","male","fiu","fiú","boy","m"].includes(g)) return "male";
  return "neutral";
};

const NeutralAvatar = () => (
  <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
    <circle cx="32" cy="32" r="32" fill="#E2E8F0" />
    <path d="M12 64c0-11 9-19 20-19s20 8 20 19Z" fill="#64748B" />
    <circle cx="32" cy="27" r="12" fill="#94A3B8" />
  </svg>
);

export const Avatar = ({ user, className = "" }) => {
  const gender = resolveGender(user);
  return (
    <span className={`inline-block overflow-hidden rounded-full ${className}`} data-gender={gender}>
      {gender === "female" ? <GirlAvatar /> : gender === "male" ? <BoyAvatar /> : <NeutralAvatar />}
    </span>
  );
};

const BoyAvatar = () => (
  <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
    <circle cx="32" cy="32" r="32" fill="#FFF1E6" />
    <path d="M12 64c0-11 9-19 20-19s20 8 20 19Z" fill="#F97316" />
    <circle cx="32" cy="28" r="12" fill="#F6C9A0" />
    <path d="M19 27c0-9 6-14 13-14s13 5 13 14c-2-5-6-7-13-7s-11 2-13 7Z" fill="#5B3A1E" />
    <circle cx="27.5" cy="29" r="1.6" fill="#2F2A26" />
    <circle cx="36.5" cy="29" r="1.6" fill="#2F2A26" />
    <path d="M28 35q4 3 8 0" stroke="#B4633A" strokeWidth="1.6" fill="none" strokeLinecap="round" />
  </svg>
);

const GirlAvatar = () => (
  <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
    <circle cx="32" cy="32" r="32" fill="#FFF1E6" />
    <path d="M14 64c0-10 8-18 18-18s18 8 18 18Z" fill="#F97316" />
    <path d="M17 46c-1-10 0-20 4-26 3-5 7-7 11-7s8 2 11 7c4 6 5 16 4 26l-5-2 1-13H21l1 13Z" fill="#7A4A22" />
    <circle cx="32" cy="29" r="12" fill="#F6C9A0" />
    <path d="M20 29c0-10 5-15 12-15s12 5 12 15c-2-6-6-8-12-8s-10 2-12 8Z" fill="#7A4A22" />
    <circle cx="27.5" cy="30" r="1.6" fill="#2F2A26" />
    <circle cx="36.5" cy="30" r="1.6" fill="#2F2A26" />
    <path d="M28 36q4 3 8 0" stroke="#B4633A" strokeWidth="1.6" fill="none" strokeLinecap="round" />
    <circle cx="44" cy="20" r="3" fill="#FDBA74" />
  </svg>
);

let persistedOpen = false; // survives remounts of the host page (polling / re-render)

export function ProfileMenu({ user, onLogout, children }) {
  const [open, setOpenState] = useState(persistedOpen);
  const setOpen = (v) => { const next = typeof v === "function" ? v(persistedOpen) : v; persistedOpen = next; setOpenState(next); };
  const ref = useRef(null);
  const gender = resolveGender(user);
  const current = normalize(typeof window !== "undefined" ? window.location.pathname : "/");
  const pages = SITE_PAGES.filter((pg) => !pageMatches(pg, current));

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("touchstart", close); document.removeEventListener("keydown", esc); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={ref} className="relative z-50" data-testid="profile-menu">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Profil"
        aria-expanded={open}
        className="h-11 w-11 sm:h-12 sm:w-12 overflow-hidden rounded-full ring-2 ring-white/80 shadow-md transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-white/60"
        data-testid="profile-avatar-btn"
        data-gender={gender}
      >
        {gender === "female" ? <GirlAvatar /> : gender === "male" ? <BoyAvatar /> : <NeutralAvatar />}
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-3 w-60 overflow-hidden rounded-2xl border-2 border-slate-900 bg-white text-left shadow-xl shadow-slate-900/15"
          data-testid="profile-dropdown"
        >
          <div className="px-4 py-3">
            <p className="truncate text-sm font-semibold text-slate-800" data-testid="profile-name">{user?.name}</p>
            {user?.identifier && <p className="mt-0.5 font-mono text-xs font-semibold tracking-wider text-slate-600" data-testid="profile-identifier">Azonosító: {user.identifier}</p>}
            <p className="truncate text-xs text-slate-700">{user?.email}</p>
            {children}
          </div>
          <nav className="border-t border-slate-100 py-1.5" data-testid="profile-nav">
            {pages.map(({ path, label, icon: Icon }) => (
              <a
                key={path}
                href={path}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-orange-50 hover:text-[#EA6C0A]"
                data-testid={`profile-nav-${path === "/" ? "home" : path.slice(1)}`}
              >
                <Icon size={16} className="text-[#F97316]" /> {label}
              </a>
            ))}
          </nav>
          <div className="border-t border-slate-100 py-1.5">
            <button
              type="button"
              onClick={() => { setOpen(false); onLogout?.(); }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#EA6C0A] transition-colors hover:bg-orange-50 hover:text-[#C2410C]"
              data-testid="profile-logout-btn"
            >
              <LogOut size={16} /> Kijelentkezés
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileMenu;
